import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaChartBar, 
  FaMap, 
  FaPlus, 
  FaTrash, 
  FaEdit, 
  FaSync, 
  FaClock,
  FaDownload,
  FaUpload,
  FaInfoCircle,
  FaTimes,
  FaCheck,
  FaExclamationTriangle,
  FaDatabase,
  FaCloudUploadAlt,
  FaMinus
} from 'react-icons/fa';
import { useParcelContext } from '../context/ParcelContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';

// Utility functions for proper capitalization
const capitalizeStateCode = (stateCode) => {
  return stateCode ? stateCode.toUpperCase() : '';
};

const capitalizeName = (name) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

const DashboardContainer = styled.div`
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
  position: relative;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #667eea;
`;

const DashboardTitle = styled.h1`
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DashboardCloseButton = styled.button`
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  
  &:hover {
    background: #c82333;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const DashboardCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e0e0e0;
`;

const CardTitle = styled.h3`
  color: #333;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
  border: 1px solid #e9ecef;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #667eea;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const CountyList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const CountyItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  background: #fafafa;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const CountyInfo = styled.div`
  flex: 1;
`;

const CountyName = styled.div`
  font-weight: 600;
  color: #333;
`;

const CountyStats = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  
  &.primary {
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a6fd8;
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
    }
  }
  
  &.danger {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
  
  &.success {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  
  &.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  &.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
  
  &.info {
    background: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  }
`;

const Dashboard = ({ onClose }) => {
  const { states, counties } = useParcelContext();
  const { addToast, removeToast, updateProgressToast, createProgressToast } = useToast();
  
  // State management
  const [dashboardData, setDashboardData] = useState({
    totalParcels: 0,
    totalCounties: 0,
    overlappingParcels: 0,
    lastUpdate: null
  });
  
  // County analysis state
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [countyAnalysis, setCountyAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [jurisdictionData, setJurisdictionData] = useState(null);
  
  const [countyData, setCountyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Modal states
  const [showUpdateCycle, setShowUpdateCycle] = useState(false);
  const [editingCounty, setEditingCounty] = useState(null);
  
  // New modal states for county registration and updates
  const [showDataAvailability, setShowDataAvailability] = useState(false);
  const [showRegisterCounty, setShowRegisterCounty] = useState(false);
  const [showUpdateCountyData, setShowUpdateCountyData] = useState(false);
  
  const [updateCycle, setUpdateCycle] = useState({
    enabled: false,
    frequency: 'daily',
    time: '02:00'
  });

  // New form states for county registration and updates
  const [dataAvailabilityForm, setDataAvailabilityForm] = useState({
    stateCode: '',
    stateName: '',
    countyName: ''
  });

  const [registerCountyForm, setRegisterCountyForm] = useState({
    stateCode: 'VA',
    stateName: 'Virginia',
    countyName: '',
    fluLandUseColumn: 'name',
    fluDescriptionColumn: 'name'
  });

  const [updateCountyDataForm, setUpdateCountyDataForm] = useState({
    stateCode: '',
    stateName: '',
    countyName: '',
    updateType: 'flu',
    fluLandUseColumn: '',
    fluDescriptionColumn: ''
  });

  // Data availability check result
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [registeringCounty, setRegisteringCounty] = useState(false);
  const [updatingCountyData, setUpdatingCountyData] = useState(false);

  // Load county data
  const loadCountyData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getCountyData();
      // Handle different response formats
      const counties = Array.isArray(response) ? response : 
                     (response.counties ? response.counties : 
                     (response.data ? response.data : []));
      setCountyData(counties);
    } catch (error) {
      setError('Failed to load county data');
      console.error('County data error:', error);
      setCountyData([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  // Update county
  const handleUpdateCounty = async (countyId, updatedData) => {
    setLoading(true);
    setError(null);
    
    try {
      await api.updateCounty(updatedData.stateCode, updatedData.name, updatedData);
      setSuccess('County updated successfully');
      setEditingCounty(null);
      loadCountyData();
    } catch (error) {
      setError('Failed to update county');
      console.error('Update county error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete county
  const handleDeleteCounty = async (countyId) => {
    if (!window.confirm('Are you sure you want to delete this county?\n\nRe-adding a county may take a long time.\n\n Do you still want to remove it?')) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Find the county data to get state code and name
      const county = countyData.find(c => 
        c.id === countyId || 
        `${c.state_code}_${c.county_name}` === countyId
      );
      if (!county) {
        throw new Error('County not found');
      }
      
      await api.deleteCounty(county.state_code, county.county_name);
      setSuccess('County deleted successfully');
      loadCountyData();
    } catch (error) {
      setError('Failed to delete county');
      console.error('Delete county error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Set update cycle
  const handleSetUpdateCycle = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await api.setUpdateCycle(updateCycle);
      setSuccess('Update cycle configured successfully');
      setShowUpdateCycle(false);
    } catch (error) {
      setError('Failed to configure update cycle');
      console.error('Set update cycle error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Analyze county parcels
  const handleAnalyzeCounty = async () => {
    if (!selectedState || !selectedCounty) {
      setError('Please select both state and county');
      return;
    }

    setAnalyzing(true);
    setError(null);
    
    try {
      const jurisdictionData = await api.getJurisdictionData(selectedState, selectedCounty);
      setJurisdictionData(jurisdictionData);
      setSuccess('County analysis completed successfully');
    } catch (error) {
      setError('Failed to analyze county parcels');
      console.error('County analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  // Check data availability
  const handleCheckDataAvailability = async () => {
    if (!dataAvailabilityForm.stateCode || !dataAvailabilityForm.stateName || !dataAvailabilityForm.countyName) {
      setError('Please fill in all required fields');
      return;
    }

    setCheckingAvailability(true);
    setError(null);
    
    try {
      const result = await api.checkCountyDataAvailability(
        dataAvailabilityForm.stateCode,
        dataAvailabilityForm.stateName,
        dataAvailabilityForm.countyName
      );
      setAvailabilityResult(result);
      setSuccess('Data availability check completed');
    } catch (error) {
      setError('Failed to check data availability');
      console.error('Data availability check error:', error);
    } finally {
      setCheckingAvailability(false);
    }
  };

  // Manual task status check after 10 seconds
  const checkTaskStatusAfterDelay = async (taskId, taskType, countyName, stateName, updateType = null) => {
    setTimeout(async () => {
      try {
        const taskStatus = await api.checkTaskStatus(taskId);
        console.log(`Manual ${taskType} task status check for ${taskId}:`, taskStatus);
        
        // Update the progress toast with the current status
        updateProgressToast(
          null, // We don't have the toast ID here, so we'll find it by taskId
          'info',
          `${taskType} in Progress`,
          taskStatus.message || `Processing ${capitalizeName(countyName)}, ${capitalizeName(stateName)}...`,
          taskStatus.progress_percentage || 0
        );
        
      } catch (error) {
        console.error(`Error checking ${taskType} task status:`, error);
      }
    }, 10000);
  };

  // Register county with data
  const handleRegisterCounty = async () => {
    if (!registerCountyForm.stateCode || !registerCountyForm.stateName || !registerCountyForm.countyName) {
      setError('Please fill in all required fields');
      return;
    }

    // Close modal immediately
    setShowRegisterCounty(false);
    
    try {
      const result = await api.registerCountyWithData(
        registerCountyForm.stateCode,
        registerCountyForm.stateName,
        registerCountyForm.countyName,
        registerCountyForm.fluLandUseColumn || null,
        registerCountyForm.fluDescriptionColumn || null
      );
      
      // Check if we got a task ID (background task)
      if (result.task_id) {
        console.log('Background task started with ID:', result.task_id);
        
        // Create self-contained progress toast
        createProgressToast(
          result.task_id,
          'County Registration in Progress',
          result.message || `Registering ${capitalizeName(registerCountyForm.countyName)}, ${capitalizeName(registerCountyForm.stateName)}...`,
          // onComplete callback
          (taskStatus) => {
            // Show success toast
            addToast('success', 'County Registration Complete', `${capitalizeName(registerCountyForm.countyName)}, ${capitalizeName(registerCountyForm.stateName)} has been successfully registered with data import.`, 50000);
            
            // Reset form
            setRegisterCountyForm({
              stateCode: 'VA',
              stateName: 'Virginia',
              countyName: '',
              fluLandUseColumn: 'name',
              fluDescriptionColumn: 'name'
            });
            
            // Reload county data
            loadCountyData();
            
            // Show completion message
            setTimeout(() => {
              addToast('success', 'Process Completed', 'County registration has been completed successfully. You can now view the county in the management list.', 3000);
            }, 2000);
          },
          // onError callback
          (errorMessage) => {
            // Show error toast
            addToast('error', 'County Registration Failed', `Failed to register ${capitalizeName(registerCountyForm.countyName)}, ${capitalizeName(registerCountyForm.stateName)}: ${errorMessage}`, 8000);
          }
        );
        
        // Also manually check task status after 10 seconds
        checkTaskStatusAfterDelay(
          result.task_id, 
          'County Registration', 
          registerCountyForm.countyName, 
          registerCountyForm.stateName
        );
        
      } else {
        // Immediate completion (no background task)
        // Show success toast
        addToast('success', 'County Registration Complete', `${capitalizeName(registerCountyForm.countyName)}, ${capitalizeName(registerCountyForm.stateName)} has been successfully registered with data import.`, 50000);
        
        // Reset form
        setRegisterCountyForm({
          stateCode: 'VA',
          stateName: 'Virginia',
          countyName: '',
          fluLandUseColumn: 'name',
          fluDescriptionColumn: 'name'
        });
        
        // Reload county data
        loadCountyData();
        
        // Show completion message
        setTimeout(() => {
          addToast('success', 'Process Completed', 'County registration has been completed successfully. You can now view the county in the management list.', 3000);
        }, 2000);
      }
      
    } catch (error) {
      // Show error toast
      addToast('error', 'County Registration Failed', `Failed to register ${capitalizeName(registerCountyForm.countyName)}, ${capitalizeName(registerCountyForm.stateName)}: ${error.message}`, 8000);
      
      console.error('Register county error:', error);
    }
  };

  // Update county data
  const handleUpdateCountyData = async () => {
    if (!updateCountyDataForm.stateCode || !updateCountyDataForm.stateName || !updateCountyDataForm.countyName) {
      setError('Please fill in all required fields');
      return;
    }

    // Close modal immediately
    setShowUpdateCountyData(false);
    
    try {
      const result = await api.updateCountyData(
        updateCountyDataForm.stateCode,
        updateCountyDataForm.stateName,
        updateCountyDataForm.countyName,
        updateCountyDataForm.updateType,
        updateCountyDataForm.fluLandUseColumn || null,
        updateCountyDataForm.fluDescriptionColumn || null
      );
      
      // Check if we got a task ID (background task)
      if (result.task_id) {
        console.log('Background update task started with ID:', result.task_id);
        
        // Create self-contained progress toast
        createProgressToast(
          result.task_id,
          'County Data Update in Progress',
          result.message || `Updating ${updateCountyDataForm.updateType} data for ${capitalizeName(updateCountyDataForm.countyName)}, ${capitalizeName(updateCountyDataForm.stateName)}...`,
          // onComplete callback
          (taskStatus) => {
            // Show success toast
            addToast('success', 'County Data Update Complete', `${capitalizeName(updateCountyDataForm.countyName)}, ${capitalizeName(updateCountyDataForm.stateName)} ${updateCountyDataForm.updateType} data has been successfully updated.`, 50000);
            
            // Reset form
            setUpdateCountyDataForm({
              stateCode: '',
              stateName: '',
              countyName: '',
              updateType: 'flu',
              fluLandUseColumn: '',
              fluDescriptionColumn: ''
            });
            
            // Reload county data
            loadCountyData();
            
            // Show completion message
            setTimeout(() => {
              addToast('success', 'Process Completed', `County ${updateCountyDataForm.updateType} data update has been completed successfully. The updated data is now available.`, 3000);
            }, 2000);
          },
          // onError callback
          (errorMessage) => {
            // Show error toast
            addToast('error', 'County Data Update Failed', `Failed to update ${updateCountyDataForm.updateType} data for ${capitalizeName(updateCountyDataForm.countyName)}, ${capitalizeName(updateCountyDataForm.stateName)}: ${errorMessage}`, 8000);
          }
        );
        
        // Also manually check task status after 10 seconds
        checkTaskStatusAfterDelay(
          result.task_id, 
          'County Data Update', 
          updateCountyDataForm.countyName, 
          updateCountyDataForm.stateName,
          updateCountyDataForm.updateType
        );
        
      } else {
        // Immediate completion (no background task)
        // Show success toast
        addToast('success', 'County Data Update Complete', `${capitalizeName(updateCountyDataForm.countyName)}, ${capitalizeName(updateCountyDataForm.stateName)} ${updateCountyDataForm.updateType} data has been successfully updated.`, 50000);
        
        // Reset form
        setUpdateCountyDataForm({
          stateCode: '',
          stateName: '',
          countyName: '',
          updateType: 'flu',
          fluLandUseColumn: '',
          fluDescriptionColumn: ''
        });
        
        // Reload county data
        loadCountyData();
        
        // Show completion message
        setTimeout(() => {
          addToast('success', 'Process Completed', `County ${updateCountyDataForm.updateType} data update has been completed successfully. The updated data is now available.`, 3000);
        }, 2000);
      }
      
    } catch (error) {
      // Show error toast
      addToast('error', 'County Data Update Failed', `Failed to update ${updateCountyDataForm.updateType} data for ${capitalizeName(updateCountyDataForm.countyName)}, ${capitalizeName(updateCountyDataForm.stateName)}: ${error.message}`, 8000);
      
      console.error('Update county data error:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadCountyData();
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <DashboardContainer>
      <DashboardHeader>
        <DashboardTitle>
          <FaChartBar />
          Parcel Data Dashboard
        </DashboardTitle>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {onClose && (
            <DashboardCloseButton onClick={onClose}>
              <FaTimes />
              Close Dashboard
            </DashboardCloseButton>
          )}
        </div>
      </DashboardHeader>

      {/* Alerts */}
      {success && <Alert className="success">{success}</Alert>}
      {error && <Alert className="error">{error}</Alert>}

      {/* Dashboard Stats */}
      

      {/* County Registration and Data Management */}
      <DashboardGrid>
        <DashboardCard>
          <CardTitle>
            <FaDatabase />
            County Registration
          </CardTitle>
          <div style={{ marginBottom: '1rem' }}>
            <p>Register new counties with automatic data import from S3</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Button 
              className="primary" 
              onClick={() => setShowDataAvailability(true)}
            >
              <FaCheck />
              Check Data Availability
            </Button>
            <Button 
              className="success" 
              onClick={() => setShowRegisterCounty(true)}
            >
              <FaCloudUploadAlt />
              Register County
            </Button>
          </div>
        </DashboardCard>

        <DashboardCard>
          <CardTitle>
            <FaSync />
            County Data Updates
          </CardTitle>
          <div style={{ marginBottom: '1rem' }}>
            <p>Update FLU or parcel data for existing counties</p>
          </div>
          <Button 
            className="primary" 
            onClick={() => setShowUpdateCountyData(true)}
          >
            <FaEdit />
            Update County Data
          </Button>
        </DashboardCard>
      </DashboardGrid>

      {/* County Management */}
      <DashboardCard>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <CardTitle>
            <FaMap />
            County Management
          </CardTitle>
          
        </div>
        
        <CountyList>
          {(Array.isArray(countyData) ? countyData : []).map((county) => (
            <CountyItem key={county.id || `${county.state_code}_${county.county_name}`}>
              <CountyInfo>
                <CountyName>
                  {capitalizeName(county.county_name || county.name)}, {capitalizeName(county.state_name || county.state)}
                </CountyName>
                {/* <CountyStats>
                  {county.parcelCount?.toLocaleString() || 0} parcels • 
                  Last updated: {county.lastUpdate ? new Date(county.lastUpdate).toLocaleDateString() : 'Never'}
                </CountyStats> */}
              </CountyInfo>
              <ActionButtons>
                {/* <Button 
                  className="secondary" 
                  onClick={() => setEditingCounty({
                    ...county,
                    name: county.county_name || county.name,
                    state: county.state_name || county.state,
                    stateCode: county.state_code || county.stateCode
                  })}
                >
                  <FaEdit />
                </Button> */}
                <Button 
                  className="danger" 
                  onClick={() => handleDeleteCounty(county.id || `${county.state_code}_${county.county_name}`)}
                >
                  <FaTrash />
                </Button>
              </ActionButtons>
            </CountyItem>
          ))}
        </CountyList>
      </DashboardCard>

      {/* Data Availability Check Modal */}
      {showDataAvailability && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Check Data Availability</ModalTitle>
              <CloseButton onClick={() => setShowDataAvailability(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>State Code * (e.g., CA)</Label>
              <Input
                type="text"
                value={dataAvailabilityForm.stateCode}
                onChange={(e) => setDataAvailabilityForm({...dataAvailabilityForm, stateCode: capitalizeStateCode(e.target.value)})}
                placeholder="Enter state code (e.g., CA)"
                maxLength={2}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>State Name *</Label>
              <Input
                type="text"
                value={dataAvailabilityForm.stateName}
                onChange={(e) => setDataAvailabilityForm({...dataAvailabilityForm, stateName: capitalizeName(e.target.value)})}
                placeholder="Enter state name (e.g., California)"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>County Name *</Label>
              <Input
                type="text"
                value={dataAvailabilityForm.countyName}
                onChange={(e) => setDataAvailabilityForm({...dataAvailabilityForm, countyName: capitalizeName(e.target.value)})}
                placeholder="Enter county name (e.g., Orange)"
              />
            </FormGroup>
            
            {/* Availability Result */}
            {availabilityResult && (
              <div style={{ marginBottom: '1rem' }}>
                <Alert className={availabilityResult.available ? "success" : "error"}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {availabilityResult.available ? <FaCheck /> : <FaExclamationTriangle />}
                    {availabilityResult.message}
                  </div>
                  {availabilityResult.available && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                      <div>Parcel Data: {availabilityResult.parcel_data_exists ? '✓ Available' : '✗ Not Available'}</div>
                      <div>FLU Data: {availabilityResult.flu_data_exists ? '✓ Available' : '✗ Not Available'}</div>
                    </div>
                  )}
                </Alert>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                className="secondary" 
                onClick={() => setShowDataAvailability(false)}
              >
                Cancel
              </Button>
              <Button 
                className="primary" 
                onClick={handleCheckDataAvailability}
                disabled={checkingAvailability}
              >
                {checkingAvailability ? <LoadingSpinner /> : <FaCheck />}
                Check Availability
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Register County Modal */}
      {showRegisterCounty && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Register County with Data</ModalTitle>
              <CloseButton onClick={() => setShowRegisterCounty(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>State Code * (e.g., CA)</Label>
              <Input
                type="text"
                value={registerCountyForm.stateCode}
                onChange={(e) => setRegisterCountyForm({...registerCountyForm, stateCode: capitalizeStateCode(e.target.value)})}
                placeholder="Enter state code (e.g., CA)"
                maxLength={2}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>State Name *</Label>
              <Input
                type="text"
                value={registerCountyForm.stateName}
                onChange={(e) => setRegisterCountyForm({...registerCountyForm, stateName: capitalizeName(e.target.value)})}
                placeholder="Enter state name (e.g., California)"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>County Name *</Label>
              <Input
                type="text"
                value={registerCountyForm.countyName}
                onChange={(e) => setRegisterCountyForm({...registerCountyForm, countyName: capitalizeName(e.target.value)})}
                placeholder="Enter county name (e.g., Orange)"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>FLU Land Use Column (Optional)</Label>
              <Input
                type="text"
                value={registerCountyForm.fluLandUseColumn}
                onChange={(e) => setRegisterCountyForm({...registerCountyForm, fluLandUseColumn: e.target.value})}
                placeholder="e.g., land_use_code, category, code"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>FLU Description Column (Optional)</Label>
              <Input
                type="text"
                value={registerCountyForm.fluDescriptionColumn}
                onChange={(e) => setRegisterCountyForm({...registerCountyForm, fluDescriptionColumn: e.target.value})}
                placeholder="e.g., description, name, label"
              />
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                className="secondary" 
                onClick={() => setShowRegisterCounty(false)}
              >
                Cancel
              </Button>
              <Button 
                className="success" 
                onClick={handleRegisterCounty}
              >
                <FaCloudUploadAlt />
                Register County
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Update County Data Modal */}
      {showUpdateCountyData && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Update County Data</ModalTitle>
              <CloseButton onClick={() => setShowUpdateCountyData(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>State Code * (e.g., CA)</Label>
              <Input
                type="text"
                value={updateCountyDataForm.stateCode}
                onChange={(e) => setUpdateCountyDataForm({...updateCountyDataForm, stateCode: capitalizeStateCode(e.target.value)})}
                placeholder="Enter state code (e.g., CA)"
                maxLength={2}
              />
            </FormGroup>
            
            <FormGroup>
              <Label>State Name *</Label>
              <Input
                type="text"
                value={updateCountyDataForm.stateName}
                onChange={(e) => setUpdateCountyDataForm({...updateCountyDataForm, stateName: capitalizeName(e.target.value)})}
                placeholder="Enter state name (e.g., California)"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>County Name *</Label>
              <Input
                type="text"
                value={updateCountyDataForm.countyName}
                onChange={(e) => setUpdateCountyDataForm({...updateCountyDataForm, countyName: capitalizeName(e.target.value)})}
                placeholder="Enter county name (e.g., Orange)"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Update Type *</Label>
              <Select
                value={updateCountyDataForm.updateType}
                onChange={(e) => setUpdateCountyDataForm({...updateCountyDataForm, updateType: e.target.value})}
              >
                <option value="flu">FLU Data Only</option>
                <option value="parcel">Parcel Data Only</option>
                <option value="both">Both FLU and Parcel Data</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>FLU Land Use Column (Optional)</Label>
              <Input
                type="text"
                value={updateCountyDataForm.fluLandUseColumn}
                onChange={(e) => setUpdateCountyDataForm({...updateCountyDataForm, fluLandUseColumn: e.target.value})}
                placeholder="e.g., land_use_code, category, code"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>FLU Description Column (Optional)</Label>
              <Input
                type="text"
                value={updateCountyDataForm.fluDescriptionColumn}
                onChange={(e) => setUpdateCountyDataForm({...updateCountyDataForm, fluDescriptionColumn: e.target.value})}
                placeholder="e.g., description, name, label"
              />
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                className="secondary" 
                onClick={() => setShowUpdateCountyData(false)}
              >
                Cancel
              </Button>
              <Button 
                className="primary" 
                onClick={handleUpdateCountyData}
              >
                <FaEdit />
                Update County Data
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Update Cycle Modal */}
      {showUpdateCycle && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Configure Update Cycle</ModalTitle>
              <CloseButton onClick={() => setShowUpdateCycle(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>
                <input
                  type="checkbox"
                  checked={updateCycle.enabled}
                  onChange={(e) => setUpdateCycle({...updateCycle, enabled: e.target.checked})}
                />
                Enable Automatic Updates
              </Label>
            </FormGroup>
            
            <FormGroup>
              <Label>Update Frequency</Label>
              <Select
                value={updateCycle.frequency}
                onChange={(e) => setUpdateCycle({...updateCycle, frequency: e.target.value})}
                disabled={!updateCycle.enabled}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Update Time</Label>
              <Input
                type="time"
                value={updateCycle.time}
                onChange={(e) => setUpdateCycle({...updateCycle, time: e.target.value})}
                disabled={!updateCycle.enabled}
              />
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                className="secondary" 
                onClick={() => setShowUpdateCycle(false)}
              >
                Cancel
              </Button>
              <Button 
                className="primary" 
                onClick={handleSetUpdateCycle}
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : <FaClock />}
                Save Configuration
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Edit County Modal */}
      {editingCounty && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Edit County</ModalTitle>
              <CloseButton onClick={() => setEditingCounty(null)}>&times;</CloseButton>
            </ModalHeader>
            
            <FormGroup>
              <Label>County Name *</Label>
              <Input
                type="text"
                value={editingCounty.name}
                onChange={(e) => setEditingCounty({...editingCounty, name: e.target.value})}
                placeholder="Enter county name"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>State *</Label>
              <Select
                value={editingCounty.state}
                onChange={(e) => setEditingCounty({...editingCounty, state: e.target.value})}
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state} value={state}>{capitalizeName(state)}</option>
                ))}
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>State Code *</Label>
              <Input
                type="text"
                value={editingCounty.stateCode}
                onChange={(e) => setEditingCounty({...editingCounty, stateCode: capitalizeStateCode(e.target.value)})}
                placeholder="Enter state code (e.g., CA)"
                maxLength={2}
              />
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                className="secondary" 
                onClick={() => setEditingCounty(null)}
              >
                Cancel
              </Button>
              <Button 
                className="success" 
                onClick={() => handleUpdateCounty(editingCounty.id, editingCounty)}
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : <FaEdit />}
                Update County
              </Button>
            </div>
          </ModalContent>
        </Modal>
      )}
    </DashboardContainer>
  );
};

export default Dashboard; 